import { memberDisplayName, type OrgMember, type WorkspaceGrant } from '@noconflict/contracts';
import { EllipsisIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgGrants, useOrgMembers } from '../../entities/member';
import { MemberRoleSelect } from '../../features/member/change-role';
import { InviteMemberDialog } from '../../features/member/invite-member';
import { ManageGrantsDialog } from '../../features/member/manage-grants';
import { PendingInvitations } from '../../features/member/pending-invitations';
import { RemoveMemberDialog } from '../../features/member/remove-member';
import { TransferOwnershipDialog } from '../../features/member/transfer-ownership';
import { Badge } from '../../shared/ui/badge';
import { Button } from '../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/card';
import { DataTable, type DataTableColumn } from '../../shared/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../shared/ui/dropdown-menu';
import { ErrorState } from '../../shared/ui/error-state';
import { Skeleton } from '../../shared/ui/skeleton';

type WorkspaceRef = { id: string; name: string; archived_at: string | null };

/** Members tab (`members.manage`): invite, open invitations, roles, workspace access, transfer, remove. */
export function MembersPanel({
  org,
  currentUserId,
  callerIsOwner,
  workspaces,
}: {
  org: { id: string; name: string };
  currentUserId: string;
  callerIsOwner: boolean;
  workspaces: readonly WorkspaceRef[];
}) {
  const { t } = useTranslation('member');
  const members = useOrgMembers(org.id);
  const grants = useOrgGrants(org.id);
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [transferring, setTransferring] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const byId = (id: string | null) => members.data?.find((m) => m.user_id === id) ?? null;

  const columns = useMemo<DataTableColumn<OrgMember>[]>(
    () => [
      {
        id: 'name',
        header: t('columns.name'),
        sortable: true,
        value: (member) => memberDisplayName(member).toLowerCase(),
        cell: (member) => (
          <span className="flex items-center gap-2 font-bold whitespace-nowrap text-heading">
            {memberDisplayName(member)}
            {member.user_id === currentUserId ? <Badge size="compact">{t('you')}</Badge> : null}
          </span>
        ),
      },
      {
        id: 'email',
        header: t('columns.email'),
        sortable: true,
        value: (member) => member.email,
        cell: (member) => <span className="whitespace-nowrap">{member.email}</span>,
      },
      {
        id: 'role',
        header: t('columns.role'),
        sortable: true,
        value: (member) => member.role,
        cell: (member) => (
          <MemberRoleSelect
            org={org}
            member={member}
            callerIsOwner={callerIsOwner}
            onError={setError}
          />
        ),
      },
      {
        id: 'access',
        header: t('columns.access'),
        value: () => null,
        cell: (member) => <AccessSummary member={member} grants={grants.data ?? []} />,
      },
      {
        id: 'actions',
        header: t('columns.actions'),
        value: () => null,
        align: 'right',
        cell: (member) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('actionsFor', { name: memberDisplayName(member) })}
              >
                <EllipsisIcon aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setManaging(member.user_id)}>
                {t('manageAccess')}
              </DropdownMenuItem>
              {callerIsOwner && member.user_id !== currentUserId ? (
                <DropdownMenuItem onSelect={() => setTransferring(member.user_id)}>
                  {t('transfer.action')}
                </DropdownMenuItem>
              ) : null}
              {member.user_id !== currentUserId && (member.role !== 'owner' || callerIsOwner) ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setRemoving(member.user_id)}
                  >
                    {t('remove.action')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [t, org, callerIsOwner, currentUserId, grants.data],
  );

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1.5">
          <CardTitle asChild className="font-sans">
            <h2>{t('title')}</h2>
          </CardTitle>
          <CardDescription>{t('help')}</CardDescription>
        </div>
        <Button onClick={() => setInviting(true)}>{t('invite.action')}</Button>
      </CardHeader>
      <CardContent className="grid gap-6">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {members.isPending ? (
          <section aria-busy="true" aria-label={t('loading')} className="grid gap-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </section>
        ) : null}
        {members.isError ? (
          <ErrorState
            size="compact"
            title={t('errorTitle')}
            description={members.error.message}
            onRetry={() => void members.refetch()}
          />
        ) : null}
        {members.data ? (
          <DataTable
            caption={t('title')}
            columns={columns}
            data={members.data}
            getRowId={(member) => member.user_id}
            pageSize={25}
          />
        ) : null}
        <PendingInvitations org={org} workspaces={workspaces} />
      </CardContent>

      <InviteMemberDialog
        open={inviting}
        onOpenChange={setInviting}
        org={org}
        scope={{ kind: 'org', callerIsOwner }}
        workspaces={workspaces}
      />

      <ManageGrantsDialog
        org={org}
        member={byId(managing)}
        grants={grants.data ?? []}
        workspaces={workspaces}
        onOpenChange={(open) => (open ? null : setManaging(null))}
      />
      <RemoveMemberDialog
        org={org}
        member={byId(removing)}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
      />
      <TransferOwnershipDialog
        org={org}
        currentUserId={currentUserId}
        member={byId(transferring)}
        onOpenChange={(open) => (open ? null : setTransferring(null))}
      />
    </Card>
  );
}

function AccessSummary({
  member,
  grants,
}: {
  member: OrgMember;
  grants: readonly WorkspaceGrant[];
}) {
  const { t } = useTranslation('member');
  if (member.role === 'owner' || member.role === 'admin') return <>{t('access.everything')}</>;
  const own = grants.filter((g) => g.user_id === member.user_id);
  const all = own.find((g) => g.workspace_id === null);
  const single = own.filter((g) => g.workspace_id !== null).length;
  const parts = [
    ...(all ? [t('access.all', { role: t(`roles.${all.role}`) })] : []),
    ...(single > 0 ? [t('access.some', { count: single })] : []),
  ];
  return <>{parts.length > 0 ? parts.join(' · ') : t('access.none')}</>;
}
